import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { JobService } from "./job.service";
import { IssueService } from "./issue.service";
import { createSignedStorageThumbnailUrl } from "@/lib/storageImageUrls";
import type { Issue, Job } from "@/data/types";
import { toUiQASubmission } from "@/mappers/qa.mapper";
import { validateQAPrerequisites, formatWarningMessages } from "@/data/qaBlockers";

const BUCKET = "qa-photos";

// ── DB row shape — matches qa_checklists table + added columns ─────────────
export interface QASubmissionRow {
  id: string;
  submission_id: string | null;
  job_id: string;
  job_number: string | null;
  inspector_name: string;
  arrival_time: string | null;
  customer_present: boolean | null;
  submitted_at: string | null;
  overall_status: string | null;             // 'Pass' | 'Fail' | 'Pending'
  checklist_items: QAChecklist | null;       // the 6 boolean items
  photos: Record<string, string> | null;     // key → storage URL
  notes: string | null;
  issues_found: QAIssueFound[] | null;
  issue_encountered: boolean | null;
  issue_notes: string | null;
  job_complete: boolean | null;
  unresolved_issues: boolean | null;
  materials_used: string | null;
  final_comments: string | null;
  status: "Draft" | "Submitted";
  updated_at: string | null;
}

export interface QAChecklist {
  drainCorrect: boolean;
  noLeaks: boolean;
  glassAligned: boolean;
  thresholdPitched: boolean;
  areaCleaned: boolean;
  protectionRemoved: boolean;
}

export interface QAIssueFound {
  type: string;
  description: string;
  severity: string;
}

// ── Frontend shape — mirrors QAForm state ──────────────────────────────────
export interface QASubmission {
  id?: string;                               // UUID from DB (set after create)
  submissionId: string;                      // QA-001
  jobId: string;                             // job_number (JOB-001)
  installerName: string;
  arrivalTime: string;
  customerPresent: boolean | null;
  checklist: QAChecklist;
  photos: Record<string, string>;            // key → storage URL or local blob URL
  issueEncountered: boolean | null;
  issueNotes: string;
  jobComplete: boolean | null;
  unresolvedIssues: boolean | null;
  materialsUsed: string;
  finalComments: string;
  status: "Draft" | "Submitted";
  submittedAt?: string;
}

// ── Upload a single photo file to Supabase Storage ────────────────────────
// Returns the storage path on success (not a URL — bucket is private).
// The caller is responsible for generating a signed URL when needed for display.
export async function uploadQAPhoto(
  file: File,
  jobNumber: string,
  submissionId: string,
  photoKey: string
): Promise<string | null> {
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = `${jobNumber}/${submissionId}/${photoKey}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true });

  if (error) {
    console.error("[QAService] Photo upload failed:", error.message);
    return null;
  }

  // Return the storage path — stored in DB, not a URL.
  return path;
}

// ── Convert stored photo paths to signed URLs (1-hour expiry) ─────────────
// Handles both plain paths ("JOB-001/QA-001/drop_cloths.jpg") and legacy
// public URLs stored before the bucket was made private.
export async function resolveQAPhotoUrls(
  photos: Record<string, string>
): Promise<Record<string, string>> {
  const entries = Object.entries(photos).filter(([, v]) => !!v);
  if (entries.length === 0) return {};

  const resolved = await Promise.all(
    entries.map(async ([key, pathOrUrl]) => {
      // Extract path from legacy public URL if needed
      const storagePath = pathOrUrl.includes(`/storage/v1/object/public/${BUCKET}/`)
        ? pathOrUrl.split(`/storage/v1/object/public/${BUCKET}/`)[1]
        : pathOrUrl;

      const signedUrl = await createSignedStorageThumbnailUrl(storagePath, {
        bucket: BUCKET,
        expiresIn: 3600,
      }).catch(() => "");

      return [key, signedUrl] as [string, string];
    })
  );

  return Object.fromEntries(resolved.filter(([, v]) => !!v));
}

// ── Service ───────────────────────────────────────────────────────────────
export const QAService = {
  async getAll(): Promise<{ data: QASubmission[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("qa_checklists")
      .select("*")
      .order("submitted_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as QASubmissionRow[]).map(toUiQASubmission), error: null };
  },

  async getByJobNumber(jobNumber: string): Promise<{ data: QASubmission[] | null; error: unknown }> {
    const { data: jobRow } = await supabase
      .from("jobs")
      .select("id")
      .eq(jobColumnFor(jobNumber), jobNumber)
      .single();

    if (!jobRow) return { data: [], error: null };

    const { data, error } = await supabase
      .from("qa_checklists")
      .select("*")
      .eq("job_id", jobRow.id)
      .order("submitted_at", { ascending: false });

    if (error || !data) return { data: null, error };

    const submissions = await Promise.all(
      (data as QASubmissionRow[]).map(async (row) => {
        const ui = toUiQASubmission(row);
        if (ui.photos && Object.keys(ui.photos).length > 0) {
          ui.photos = await resolveQAPhotoUrls(ui.photos);
        }
        return ui;
      })
    );
    return { data: submissions, error: null };
  },

  async getById(submissionId: string): Promise<{ data: QASubmission | null; error: unknown }> {
    const { data, error } = await supabase
      .from("qa_checklists")
      .select("*")
      .eq("submission_id", submissionId)
      .single();

    if (error || !data) return { data: null, error };

    const ui = toUiQASubmission(data as QASubmissionRow);
    if (ui.photos && Object.keys(ui.photos).length > 0) {
      ui.photos = await resolveQAPhotoUrls(ui.photos);
    }
    return { data: ui, error: null };
  },

  /** Save a draft — called when the form is partially filled */
  async saveDraft(submission: QASubmission): Promise<{ data: QASubmission | null; error: unknown }> {
    const [{ data: jobRow }, organizationId] = await Promise.all([
      supabase
        .from("jobs")
        .select("id, job_number")
        .eq(jobColumnFor(submission.jobId), submission.jobId)
        .single(),
      getCurrentOrganizationId().catch(() => null),
    ]);

    if (!jobRow) return { data: null, error: new Error(`Job not found: ${submission.jobId}`) };

    const row = {
      submission_id: submission.submissionId,
      job_id: jobRow.id,
      job_number: jobRow.job_number ?? submission.jobId,
      inspector_name: submission.installerName,
      arrival_time: submission.arrivalTime || null,
      customer_present: submission.customerPresent,
      overall_status: "Pending",
      checklist_items: submission.checklist,
      photos: submission.photos,
      notes: submission.finalComments || null,
      issues_found: submission.issueEncountered && submission.issueNotes
        ? [{ type: "Install Issue", description: submission.issueNotes, severity: "Medium" }]
        : [],
      issue_encountered: submission.issueEncountered,
      issue_notes: submission.issueNotes || null,
      job_complete: submission.jobComplete,
      unresolved_issues: submission.unresolvedIssues,
      materials_used: submission.materialsUsed || null,
      final_comments: submission.finalComments || null,
      status: "Draft" as const,
      organization_id: organizationId,
    };

    const { data, error } = await supabase
      .from("qa_checklists")
      .upsert([row], { onConflict: "submission_id" })
      .select()
      .single();

    if (error || !data) {
      console.error("[QAService.saveDraft] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiQASubmission(data as QASubmissionRow), error: null };
  },

  /** Final submit — saves the QA, updates job stage, creates issue if needed */
  async submit(submission: QASubmission): Promise<{ data: QASubmission | null; error: unknown }> {
    const [{ data: jobRow }, organizationId] = await Promise.all([
      supabase
        .from("jobs")
        .select("id, job_number")
        .eq(jobColumnFor(submission.jobId), submission.jobId)
        .single(),
      getCurrentOrganizationId().catch(() => null),
    ]);

    if (!jobRow) return { data: null, error: new Error(`Job not found: ${submission.jobId}`) };

    // Fetch full job for QA blocker validation
    const fullJobResult = await JobService.getById(submission.jobId);
    if (fullJobResult.error) {
      console.warn("[QAService.submit] could not fetch job for blocker check:", fullJobResult.error);
    } else {
      const job = fullJobResult.data;
      const readiness = validateQAPrerequisites(job);
      if (!readiness.canProceed) {
        return {
          data: null,
          error: new Error(`QA blocked: ${readiness.blockers.map((b) => b.label).join(" · ")}`),
        };
      }
      const warnings = formatWarningMessages(job);
      if (warnings.length > 0) {
        console.info(`[QAService.submit] QA warnings for job ${submission.jobId}:`, warnings);
      }
    }

    const now = new Date().toISOString();

    // Cross-check open issues in DB — installer self-report alone is not sufficient
    const { count: openIssueCount } = await supabase
      .from("issues")
      .select("id", { count: "exact", head: true })
      .eq("job_id", jobRow.id)
      .in("status", ["Open", "In Progress"]);
    const hasOpenIssues = (openIssueCount ?? 0) > 0;

    const row = {
      submission_id: submission.submissionId,
      job_id: jobRow.id,
      job_number: jobRow.job_number ?? submission.jobId,
      inspector_name: submission.installerName,
      arrival_time: submission.arrivalTime || null,
      customer_present: submission.customerPresent,
      submitted_at: now,
      overall_status: (submission.unresolvedIssues || hasOpenIssues) ? "Fail" : "Pass",
      checklist_items: submission.checklist,
      photos: submission.photos,
      notes: submission.finalComments || null,
      issues_found: submission.issueEncountered && submission.issueNotes
        ? [{ type: "Install Issue", description: submission.issueNotes, severity: "Medium" }]
        : [],
      issue_encountered: submission.issueEncountered,
      issue_notes: submission.issueNotes || null,
      job_complete: submission.jobComplete,
      unresolved_issues: submission.unresolvedIssues,
      materials_used: submission.materialsUsed || null,
      final_comments: submission.finalComments || null,
      status: "Submitted" as const,
      organization_id: organizationId,
    };

    // 2. Upsert QA record
    const { data, error } = await supabase
      .from("qa_checklists")
      .upsert([row], { onConflict: "submission_id" })
      .select()
      .single();

    if (error || !data) {
      console.error("[QAService.submit] error:", JSON.stringify(error));
      return { data: null, error };
    }

    // 3. Update job stage to QA Review
    await JobService.update(submission.jobId, {
      currentStage: "QA Review",
      qaSubmitted: true,
      qaStatus: "Submitted",
      issueFlag: submission.issueEncountered ?? false,
      installStatus: "Install Complete",
    });

    // 4. Create issue if one was encountered
    if (submission.issueEncountered && submission.issueNotes) {
      const issue: Issue = {
        id: `ISS-${Date.now()}`, // temporary — will be replaced by DB sequence
        jobId: submission.jobId,
        issueType: "Install Issue",
        severity: "Medium",
        description: submission.issueNotes,
        photo: submission.photos["issue"] ?? "",
        owner: submission.installerName,
        status: "Open",
        dateCreated: now.split("T")[0],
        source: "QA Inspector",
        resolved: undefined
      };
      await IssueService.create(issue);
    }

    return { data: toUiQASubmission(data as QASubmissionRow), error: null };
  },

  async delete(submissionId: string): Promise<{ error: unknown }> {
    const { error } = await supabase
      .from("qa_checklists")
      .delete()
      .eq("submission_id", submissionId);
    return { error };
  },

  async getQAQueue(): Promise<{ data: QASubmission[] | null; error: unknown }> {
    const { data: jobRows, error: jobError } = await supabase
      .from("jobs")
      .select("id")
      .eq("currentStage", "QA Review");

    if (jobError || !jobRows || jobRows.length === 0) return { data: [], error: null };

    const jobIds = jobRows.map((j) => j.id);

    const { data: qaRows, error: qaError } = await supabase
      .from("qa_checklists")
      .select("*")
      .in("job_id", jobIds)
      .eq("status", "Submitted")
      .order("submitted_at", { ascending: false });

    if (qaError || !qaRows) return { data: null, error: qaError };

    const submissions = await Promise.all(
      (qaRows as QASubmissionRow[]).map(async (row) => {
        const ui = toUiQASubmission(row);
        if (ui.photos && Object.keys(ui.photos).length > 0) {
          ui.photos = await resolveQAPhotoUrls(ui.photos);
        }
        return ui;
      })
    );

    return { data: submissions, error: null };
  },
};
